import React from 'react';

const Help = ({ isOpen, onClose, currentOperation }) => {
  if (!isOpen) return null;

  const getOperationInstructions = () => {
    switch (currentOperation) {
      case 'addition':
        return {
          title: 'Addition Practice Help',
          description: 'Practice addition with step-by-step working and carry-over calculations',
          howToSolve: [
            'Add digits from right to left (ones place first)',
            'If the sum is 10 or more, write the ones digit and carry the tens digit',
            'Add the carried digit to the next column',
            'Continue until all digits are added'
          ],
          colorCoding: [
            { color: '#e8f4fd', border: '#ddd', label: 'First & Second Numbers' },
            { color: '#fff3e0', border: '#e67e22', label: 'Carry Values' },
            { color: '#d4edda', border: '#28a745', label: 'Answer Digits (Correct)' },
            { color: '#f8d7da', border: '#dc3545', label: 'Answer Digits (Incorrect)' }
          ]
        };
      
      case 'subtraction':
        return {
          title: 'Subtraction Practice Help',
          description: 'Practice subtraction with step-by-step working and borrowing calculations',
          howToSolve: [
            'Subtract digits from right to left (ones place first)',
            'If the top digit is smaller than the bottom digit, borrow from the left',
            'After borrowing, the top digit becomes 10 + the original digit',
            'Subtract and write the result',
            'Continue until all digits are subtracted'
          ],
          colorCoding: [
            { color: '#e8f4fd', border: '#ddd', label: 'First & Second Numbers' },
            { color: '#ffe6cc', border: '#ff8c00', label: 'After Borrowing (2 digits)' },
            { color: '#e6f3ff', border: '#0066cc', label: 'After Reduction' },
            { color: '#d4edda', border: '#28a745', label: 'Answer Digits (Correct)' },
            { color: '#f8d7da', border: '#dc3545', label: 'Answer Digits (Incorrect)' }
          ]
        };
      
      case 'multiplication':
        return {
          title: 'Multiplication Practice Help',
          description: 'Practice long multiplication with step-by-step working and carrying',
          howToSolve: [
            'Multiply each digit of the second number by the first number',
            'Start from the rightmost digit (ones place)',
            'Write partial products below, shifted left for each place value',
            'Add all partial products to get the final answer',
            'Carry over any values greater than 9'
          ],
          colorCoding: [
            { color: '#e8f4fd', border: '#ddd', label: 'First & Second Numbers' },
            { color: '#fff3e0', border: '#f39c12', label: 'Partial Products' },
            { color: '#ffe6cc', border: '#ff8c00', label: 'Carry Values' },
            { color: '#d4edda', border: '#28a745', label: 'Answer Digits (Correct)' },
            { color: '#f8d7da', border: '#dc3545', label: 'Answer Digits (Incorrect)' }
          ]
        };
      
      case 'division':
        return {
          title: 'Division Practice Help',
          description: 'Practice long division with step-by-step working',
          howToSolve: [
            'Write the dividend inside the division symbol and the divisor outside',
            'Find how many times the divisor goes into the dividend',
            'Write the quotient digit above the dividend',
            'Multiply the divisor by the quotient digit and write the result below',
            'Subtract to find the remainder'
          ],
          colorCoding: [
            { color: '#ffeaea', border: '#e74c3c', label: 'Quotient Digits' },
            { color: '#e8f5e8', border: '#27ae60', label: 'Partial Products' },
            { color: '#fff3e0', border: '#f39c12', label: 'Remainders' }
          ]
        };
      
      default:
        return {
          title: 'Math Practice Help',
          description: 'Welcome to BellMaths! Choose an operation to get started.',
          howToSolve: [],
          colorCoding: []
        };
    }
  };

  const instructions = getOperationInstructions();

  return (
    <div className="help-overlay" onClick={onClose}>
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="help-header">
          <h2>{instructions.title}</h2>
          <button className="help-close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="help-content">
          <p className="help-description">{instructions.description}</p>
          
          <div className="help-notebook-instructions">
            <h3>Notebook Instructions</h3>
            <ol>
                              <li><strong>Choose your level</strong> for appropriate difficulty</li>
              <li><strong>Use custom numbers</strong> or generate random problems</li>
              <li><strong>Type your answer</strong> digit by digit in the boxes below</li>
              <li><strong>Use Tab/Shift+Tab</strong> to navigate between boxes</li>
              <li><strong>Click "Check Answer"</strong> to verify your solution</li>
              <li><strong>Click "Show Solution"</strong> to see step-by-step working</li>
            </ol>
          </div>
          
          {instructions.howToSolve.length > 0 && (
            <div className="help-section">
              <h3>How to solve:</h3>
              <ol>
                {instructions.howToSolve.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>
          )}
          
          {instructions.colorCoding.length > 0 && (
            <div className="help-section">
              <h3>Color Coding:</h3>
              <div className="color-grid">
                {instructions.colorCoding.map((item, index) => (
                  <div key={index} className="color-item">
                    <span 
                      className="color-box"
                      style={{ 
                        backgroundColor: item.color, 
                        border: `1px solid ${item.border}` 
                      }}
                    ></span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="help-tips">
            <h3>Platform Tips:</h3>
            <ul>
              <li>Use the virtual keyboard for easy input</li>
              <li>Click "Check Answer" to validate your solution</li>
              <li>Click "Show Solution" to see the step-by-step working</li>
                              <li>Change level to adjust difficulty</li>
              <li>Use custom numbers for specific practice</li>
              <li>Generate new problems anytime with the button</li>
            </ul>
          </div>


        </div>
      </div>
    </div>
  );
};

export default Help;
